import type { Lake } from "@divespots/contracts";
import { LakeUpdate, pickLocale } from "@divespots/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.ts";
import { useLakes } from "../api/index.ts";
import { Button } from "../components/Button.tsx";
import { formatMetres } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { Field } from "./ui.tsx";

/** Admin only: the reference water level of each lake. Changing it recomputes object depths. */
export function LakesPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const lakes = useLakes();
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <div className="grid gap-6">
      <h1 className="ds-text-title-1 m-0">{t("admin.lakes.title")}</h1>
      {lakes.isPending ? <p role="status">{t("common.loading")}</p> : null}
      {lakes.isError ? (
        <p className="adm-error" role="alert">
          {t("admin.err.load")}
        </p>
      ) : null}
      <ul className="ds-list adm-list" aria-label={t("admin.lakes.title")}>
        {(lakes.data ?? []).map((l) => (
          <li key={l.slug} className="adm-lakerow">
            <div className="ds-row">
              <span className="ds-row__title">
                <span className="ds-text-headline">{pickLocale(l.name, locale) ?? l.slug}</span>
                <span className="ds-text-subheadline text-label-secondary block">
                  {t("admin.lakes.level")}: {formatMetres(l.waterLevelM, locale, 1)}
                </span>
              </span>
              <span className={`ds-badge${l.hasBathy ? " ds-badge--object" : ""}`}>
                {l.hasBathy ? t("admin.lakes.bathy") : t("spot.noBathy")}
              </span>
              <Button
                variant="tinted"
                aria-label={t("admin.lakes.edit", { name: pickLocale(l.name, locale) ?? l.slug })}
                onClick={() => setEditing(editing === l.slug ? null : l.slug)}
              >
                {t("admin.common.edit")}
              </Button>
            </div>
            {editing === l.slug ? <LakeEditor lake={l} onDone={() => setEditing(null)} /> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LakeEditor({ lake, onDone }: { lake: Lake; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [value, setValue] = useState(String(lake.waterLevelM));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const n = Number(value.replace(",", "."));
      const parsed = LakeUpdate.safeParse({ waterLevelM: n });
      if (value.trim() === "" || !parsed.success) throw new Error("invalid");
      await api.updateLake(lake.slug, { waterLevelM: n });
    },
    onSuccess: async () => {
      await qc.invalidateQueries();
      onDone();
    },
    onError: (e) =>
      setError(
        e instanceof Error && e.message === "invalid"
          ? t("admin.lakes.invalid")
          : t("admin.err.save"),
      ),
  });

  return (
    <form
      className="adm-subform"
      noValidate
      aria-label={t("admin.lakes.edit", { name: lake.slug })}
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <p className="adm-warning ds-text-subheadline m-0">
        <TriangleAlert aria-hidden="true" size={17} strokeWidth={1.75} />
        {t("admin.lakes.warning")}
      </p>
      <Field label={t("admin.lakes.levelInput")} error={error ?? undefined}>
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            className="adm-input ds-text-coordinate"
            inputMode="decimal"
            value={value}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </Field>
      <div className="adm-actions">
        <Button type="submit" variant="filled" disabled={save.isPending}>
          {t("admin.common.save")}
        </Button>
        <Button variant="tinted" onClick={onDone}>
          {t("admin.common.cancel")}
        </Button>
      </div>
    </form>
  );
}
